/**
 * sharingConsentPrimitives — stateless markup primitives for the
 * partner-sharing consent surfaces. `SharingDetailPanel` (via
 * `renderSharingPill`) consumes the pill; `PartnerSection`'s unlinked privacy
 * preview consumes `PRIVATE_ROWS`.
 *
 * Pure — no DOM, no host (`this`) access. This module only renders what
 * it's told to.
 *
 * @module components/sharingConsentPrimitives
 */

import { escapeHtml } from '../core/utils.js';

/**
 * What never crosses the wire, whatever depth is chosen — the list the
 * unlinked-state privacy preview (`PartnerSection`'s consent-screen FAQ) and
 * the sharing run's "How sharing works" page (`SharingDetailPanel`) both
 * render. Kept here rather than inline so the promise reads the same
 * words everywhere it is made.
 * @type {Array<string>}
 */
export const PRIVATE_ROWS = [
  'Your individual chat sessions',
  'Your personal insights',
  'Anything you mark as private'
];

/** State-pill color, keyed by the ledger's `full|basic|off|review`
 * vocabulary (design §13 decision F). Fixed allowlist — never interpolate
 * an unvalidated key into the `notification-label--{color}` class. */
const STATE_PILL_COLORS = {
  full: 'green',
  basic: 'blue',
  off: 'gray',
  review: 'orange'
};

/**
 * A single ledger state pill: `notification-label` dot + uppercase label,
 * colored per the `full|basic|off|review` mapping (decision F).
 *
 * @param {'full'|'basic'|'off'|'review'} stateKey - Must be one of the fixed allowlist keys
 * @param {string} label - Display text (e.g. "Full", "Review")
 * @returns {string} HTML string
 */
export function renderStatePill(stateKey, label) {
  const color = STATE_PILL_COLORS[stateKey] || 'gray';
  return `<span class="notification-label notification-label--${color}">${escapeHtml(label)}</span>`;
}
